import { formatMoney, formatYears } from '@components/InputSlider/helpers'
import Box from '@components/Box'
import { STypographyDefault, SH4 } from '@components/Typography'
import { above } from '@styles/mixins/breakpoints'
import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import { IValues } from '../types'
import { Collapse } from 'react-collapse'
import Arrow from '@icons/arrowUp.inline.svg'
import { SButton } from '@components/Button/Button'
import WithMedia from '@components/Utils/withMedia'
import { xl } from '@styles/variables/breakpoints'

const SIcon = styled(Box)<{ isOpened: boolean }>`
  border-radius: 100%;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.3s;
  flex-shrink: 0;
  flex-grow: 0;
  position: absolute;
  right: -24px;
  top: 50%;
  transform: translateY(-50%) rotate(180deg);
  & path {
    stroke: ${({ theme }) => theme.colors.green};
  }

  ${({ isOpened }) =>
    isOpened &&
    css`
      top: 50%;
      transform: translateY(-50%) rotate(0deg);
    `}
`

const SBigNumber = styled(STypographyDefault)`
  color: ${({ theme }) => theme.colors.green};
  font-size: 36px;
  line-height: 44px;
  font-weight: 600;
  width: 100%;

  ${above('md')(css`
    font-size: 32px;
    line-height: 40px;
  `)}

  ${above('xl')(css`
    font-size: 40px;
    line-height: 48px;
  `)}
`

const SCollapseOpener = styled(Box)`
  display: inline-flex;
  align-items: center;
  font-size: 16px;
  line-height: 24px;
  cursor: pointer;
`

const SResultButton = styled(SButton)`
  width: 100%;
  display: flex;
  justify-content: center;
  text-align: center;
  ${above('xl')(css`
    display: inline-block;
    width: auto;
  `)}
`

const SResult = styled(Box)`
  .ReactCollapse--collapse {
    transition: 0.3s;
  }
`
const SCalculateWrapper = styled(Box)`
  display: flex;
`
const PERCENT = 0.035
const MONTH_INDEX = 1.07
const RATE = { month: 12, quarter: 4, year: 1 }

const Result: React.FC<IValues & { scrollToForm: () => void }> = ({ contribution, period, rate, scrollToForm }) => {
  const [showInfo, setShowInfo] = useState(true)
  const [showMobileInfo, setShowMobileInfo] = useState(false)
  const yearContribution = contribution * RATE[rate]
  const percentContribution = yearContribution * PERCENT

  const yearBudget = percentContribution + yearContribution
  const fiveYearBudget = yearContribution * period
  const endSum = (yearContribution * MONTH_INDEX * (Math.pow(MONTH_INDEX, period) - 1)) / (MONTH_INDEX - 1)
  return (
    <SResult>
      <Box display="flex" flexWrap={{ xs: 'wrap', md: 'nowrap' }}>
        <Box
          display="flex"
          flexDirection="column"
          mb={{ xs: '20px', md: '32px' }}
          mr={{ xs: 0, md: '40px' }}
          width={{ xs: '100%', md: 'auto' }}
        >
          <STypographyDefault color="darkGray" mb="8px">
            Бюджет на сотрудника в год
          </STypographyDefault>
          <SBigNumber>{formatMoney(yearBudget.toFixed(0))}</SBigNumber>
        </Box>
        <Box display="flex" flexDirection="column" mb="32px" width={{ xs: '100%', md: 'auto' }}>
          <STypographyDefault color="darkGray" mb="8px">
            Итого через {formatYears(period)} на счете у сотрудника
          </STypographyDefault>
          <SBigNumber>{formatMoney(endSum.toFixed(0))}</SBigNumber>
        </Box>
      </Box>
      <WithMedia minWidth={xl}>
        <>
          <SCollapseOpener onClick={() => setShowInfo((prevShow) => !prevShow)}>
            <SCalculateWrapper>
              <Box position="relative">
                Подробный расчёт
                <SIcon isOpened={showInfo}>
                  <Arrow />
                </SIcon>
              </Box>
            </SCalculateWrapper>
          </SCollapseOpener>
          <Collapse isOpened={showInfo}>
            <Box pt="12px" display="flex" flexWrap={{ xs: 'wrap', md: 'nowrap' }}>
              <Box
                display="flex"
                flexDirection={{ xs: 'row', md: 'column' }}
                flexWrap={{ xs: 'wrap', md: 'nowrap' }}
                mr={{ xs: 0, md: '70px' }}
              >
                <Box mb={{ xs: '12px', xl: '20px' }} width={{ xs: '100%', md: 'auto' }}>
                  <STypographyDefault color="darkGray" mb="4px">
                    Взносы работодателя в год
                  </STypographyDefault>
                  <SH4 mb="0" fontSize="20px" lineHeight="24px">
                    {formatMoney(yearContribution.toFixed(0))}
                  </SH4>
                </Box>
                <Box mb={{ xs: '12px', md: 0 }} width={{ xs: '100%', md: 'auto' }}>
                  <STypographyDefault color="darkGray" mb="4px">
                    Целевой взнос НПФ (3.5%) в год
                  </STypographyDefault>
                  <SH4 mb="0" fontSize="20px" lineHeight="24px">
                    {formatMoney(percentContribution.toFixed(0))}
                  </SH4>
                </Box>
              </Box>
              <Box display="flex" flexDirection={{ xs: 'row', md: 'column' }} flexWrap={{ xs: 'wrap', md: 'nowrap' }}>
                <Box mb={{ xs: '12px', xl: '20px' }} width={{ xs: '100%', md: 'auto' }}>
                  <STypographyDefault color="darkGray" mb="4px">
                    Взносы работодателя за {formatYears(period)}
                  </STypographyDefault>
                  <SH4 mb="0" fontSize="20px" lineHeight="24px">
                    {formatMoney(fiveYearBudget.toFixed(0))}
                  </SH4>
                </Box>
                <Box mb={{ xs: '12px', md: 0 }} width={{ xs: '100%', md: 'auto' }}>
                  <STypographyDefault color="darkGray" mb="4px">
                    Инвестиционный доход за {formatYears(period)}
                  </STypographyDefault>
                  <SH4 mb="0" fontSize="20px" lineHeight="24px">
                    {formatMoney((endSum - fiveYearBudget).toFixed(0))}
                  </SH4>
                </Box>
              </Box>
            </Box>
          </Collapse>
        </>
      </WithMedia>
      <WithMedia maxWidth={xl}>
        <>
          <SCollapseOpener onClick={() => setShowMobileInfo((prevShow) => !prevShow)}>
            <SCalculateWrapper>
              <Box position="relative">
                Подробный расчёт
                <SIcon isOpened={showMobileInfo}>
                  <Arrow />
                </SIcon>
              </Box>
            </SCalculateWrapper>
          </SCollapseOpener>
          <Collapse isOpened={showMobileInfo}>
            <Box pt="12px" display="flex" flexWrap={{ xs: 'wrap', md: 'nowrap' }}>
              <Box
                display="flex"
                flexDirection={{ xs: 'row', md: 'column' }}
                flexWrap={{ xs: 'wrap', md: 'nowrap' }}
                mr={{ xs: 0, md: '70px' }}
              >
                <Box mb={{ xs: '12px', xl: '20px' }} width={{ xs: '100%', md: 'auto' }}>
                  <STypographyDefault color="darkGray" mb="4px">
                    Взносы работодателя в год
                  </STypographyDefault>
                  <SH4 mb="0" fontSize="20px" lineHeight="24px">
                    {formatMoney(yearContribution.toFixed(0))}
                  </SH4>
                </Box>
                <Box mb={{ xs: '12px', md: 0 }} width={{ xs: '100%', md: 'auto' }}>
                  <STypographyDefault color="darkGray" mb="4px">
                    Целевой взнос НПФ (3.5%) в год
                  </STypographyDefault>
                  <SH4 mb="0" fontSize="20px" lineHeight="24px">
                    {formatMoney(percentContribution.toFixed(0))}
                  </SH4>
                </Box>
              </Box>
              <Box display="flex" flexDirection={{ xs: 'row', md: 'column' }} flexWrap={{ xs: 'wrap', md: 'nowrap' }}>
                <Box mb={{ xs: '12px', xl: '20px' }} width={{ xs: '100%', md: 'auto' }}>
                  <STypographyDefault color="darkGray" mb="4px">
                    Взносы работодателя за {formatYears(period)}
                  </STypographyDefault>
                  <SH4 mb="0" fontSize="20px" lineHeight="24px">
                    {formatMoney(fiveYearBudget.toFixed(0))}
                  </SH4>
                </Box>
                <Box mb={{ xs: '12px', md: 0 }} width={{ xs: '100%', md: 'auto' }}>
                  <STypographyDefault color="darkGray" mb="4px">
                    Инвестиционный доход за {formatYears(period)}
                  </STypographyDefault>
                  <SH4 mb="0" fontSize="20px" lineHeight="24px">
                    {formatMoney((endSum - fiveYearBudget).toFixed(0))}
                  </SH4>
                </Box>
              </Box>
            </Box>
          </Collapse>
        </>
      </WithMedia>
      <Box>
        <SResultButton mt={{ xs: '22px', xl: '32px' }} onClick={scrollToForm}>
          Хочу начать заботиться о сотрудниках
        </SResultButton>
      </Box>
    </SResult>
  )
}

export default Result
